// Auth failures are untrusted input: even an Error.message can contain the
// request credentials echoed by a server or wallet SDK. Never redact-and-echo.
// Construct diagnostics and UI text from a closed vocabulary instead.
const ENDPOINTS = [
    '/users/profile', '/auth/token', '/auth/refresh',
    '/mpc/client', '/mpc/recover', '/mpc/refresh', '/mpc/refresh-using-token',
    '/mpc/is-session-token-valid', '/mpc/signing-share-pairs', '/mpc/backup-share-pairs',
    '/v1/generate', '/v1/backup', '/v1/recover', '/v1/raw/sign/ED25519',
];
function field(value, key) {
    try {
        return value !== null && (typeof value === 'object' || typeof value === 'function')
            ? value[key]
            : undefined;
    }
    catch {
        return undefined;
    }
}
function endpointLabel(value) {
    if (typeof value !== 'string')
        return 'unknown';
    // Do not retain origin, userinfo, query, fragment, unknown paths or versions.
    const path = value.replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/, 1)[0]
        .replace(/^\/api\/\d+(?:\.\d+)*\//, '/');
    return ENDPOINTS.find(endpoint => endpoint === path) ?? 'unknown';
}
export function getAuthDiagnostic(error, endpoint) {
    const response = field(error, 'response');
    const rawStatus = field(response, 'status');
    const status = typeof rawStatus === 'number' && Number.isInteger(rawStatus)
        && rawStatus >= 100 && rawStatus <= 599 ? rawStatus : null;
    const code = field(error, 'code');
    const rawMessage = field(error, 'message');
    // Used only to classify SDK errors without standard codes, never emitted.
    const message = typeof rawMessage === 'string' ? rawMessage : '';
    let category = 'unknown';
    if (status === 401)
        category = 'unauthorized';
    else if (status === 403)
        category = 'forbidden';
    else if (status === 408 || status === 504)
        category = 'timeout';
    else if (status === 429)
        category = 'rate_limited';
    else if (status !== null && status >= 500)
        category = 'service_unavailable';
    else if (status !== null && status >= 400)
        category = 'request_failed';
    else if (code === 4001 || code === 'ACTION_REJECTED' || /user rejected|user denied/i.test(message))
        category = 'user_rejected';
    else if (code === 'ECONNABORTED' || code === 'ETIMEDOUT' || /timed?\s*out/i.test(message))
        category = 'timeout';
    else if (code === 'ERR_NETWORK' || /network error/i.test(message))
        category = 'network';
    else if (/walletconnect|provider.*lost|provider.*disconnect/i.test(message))
        category = 'wallet_disconnected';
    else if (field(error, 'isWalletSignError') === true || message.startsWith('[WalletSign]'))
        category = 'wallet_signing';
    return {
        endpoint: endpointLabel(endpoint ?? field(field(error, 'config'), 'url')),
        status,
        category,
    };
}
const MESSAGES = {
    unauthorized: 'This wallet is not authorized.',
    forbidden: 'This wallet is not authorized.',
    timeout: 'The login request timed out. Please check your wallet and try again.',
    rate_limited: 'Too many login attempts. Please wait and try again.',
    service_unavailable: 'Login service is temporarily unavailable. Please retry.',
    request_failed: 'Login could not be completed. Please try again.',
    network: 'Unable to reach the login service. Please check your connection and try again.',
    user_rejected: 'The wallet request was cancelled. Please try again when ready.',
    wallet_disconnected: 'Wallet connection lost. Please disconnect and reconnect.',
    wallet_signing: 'Wallet signing failed. Please open your wallet and try again.',
    unknown: 'Login could not be completed. Please try again.',
};
export function authErrorMessage(error) {
    return MESSAGES[getAuthDiagnostic(error).category];
}
export function clearAuthErrorDiagnostics() {
    // Remove legacy text before any reader can surface it. Credential storage and
    // resumable MPC state are intentionally separate and must remain untouched.
    try {
        for (const key of [
            'mobile_last_auth_error', 'mobile_last_auth_error_raw',
            'mobile_last_auth_error_time', 'mobile_last_auth_error_diagnostic',
            'mobile_debug_log', 'mobile_debug_log_v2',
        ])
            sessionStorage.removeItem(key);
    }
    catch { /* Storage can be unavailable in private browsing. */ }
}
export function persistAuthFailure(error) {
    try {
        sessionStorage.removeItem('mobile_last_auth_error_raw');
        sessionStorage.setItem('mobile_last_auth_error', authErrorMessage(error));
        sessionStorage.setItem('mobile_last_auth_error_diagnostic', JSON.stringify(getAuthDiagnostic(error)));
        sessionStorage.setItem('mobile_last_auth_error_time', new Date().toISOString());
    }
    catch { /* Never log a storage exception containing the attempted value. */ }
}
